import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import ScheduleEntryWeekPicker from './ui/ScheduleEntryWeekPicker';
import DaySelector from './DaySelector';
import TimeRangeSelector from './TimeRangeSelector';
import RoomSelector from './RoomSelector';
import SuggestionList from './SuggestionList';
import { ScheduleSuggestion, SuggestionParameters } from '../types/AutoScheduling';
import Room from '../models/Room';
import Teacher from '../models/Teacher';
import Group from '../models/Group';
import Subject from '../models/Subject';
import ScheduleEntry from '../models/ScheduleEntry';
import AutoSchedulingService from '../services/AutoSchedulingService';
import { analyzeError, formatErrorMessage, logError, validateRequiredData, ErrorContext } from '../utils/errorHandling';

interface AutoSchedulerProps {
    isOpen: boolean;
    onClose: () => void;
    onSuggestionSelect: (suggestion: ScheduleSuggestion) => void;
    initialFormData: {
        teacher: string;
        subject: string;
        groups: (number | string)[];
        weekSeries: string;
        day: string;
        timeStart: string;
        timeEnd: string;
        room: string;
        year: number;
        duration?: number; // in minutes
    };
    availableRooms: Room[];
    baseScheduleItems: ScheduleEntry[];
    teachers: Teacher[];
    groups: Group[];
    subjects: Subject[];
    currentYear: number;
    activeDays: string[];
    institutionSettings: {
        minHour: number;
        maxHour: number;
    };
    // New prop to exclude current item from conflict detection
    excludeItemId?: string | number;
}

interface AutoSchedulerState {
    selectedWeeks: number[];
    selectedDays: string[];
    minHour: number;
    maxHour: number;
    selectedRooms: string[];
    weeksToSchedule: number;
    isGenerating: boolean;
    suggestions: ScheduleSuggestion[];
    error: string | null;
    sortBy: 'score' | 'time' | 'room';
    loadingProgress: number;
    loadingMessage: string;
    generationStartTime: number | null;
    hasTimedOut: boolean;
    retryCount: number;
    showFallbackOptions: boolean;
    isApplyingSuggestion: boolean;
    appliedSuggestion: ScheduleSuggestion | null;
    showConfirmation: boolean;
}

/**
 * Main container component that orchestrates the automatic scheduling workflow
 */
const AutoScheduler: React.FC<AutoSchedulerProps> = ({
    isOpen,
    onClose,
    onSuggestionSelect,
    initialFormData,
    availableRooms,
    baseScheduleItems,
    teachers,
    groups,
    subjects,
    currentYear,
    activeDays,
    institutionSettings,
    excludeItemId
}) => {
    // Helper function to parse week series string into array of week numbers
    const parseWeekSeries = useCallback((weekSeries: string): number[] => {
        const weekNumbers: number[] = [];

        if (weekSeries) {
            // Simple parsing - assumes comma-separated week numbers or ranges
            const parts = weekSeries.split(',');
            for (const part of parts) {
                const trimmed = part.trim();
                if (trimmed.includes('-')) {
                    // Range like "1-5"
                    const rangeParts = trimmed.split('-').map(n => parseInt(n.trim(), 10));
                    if (rangeParts.length === 2) {
                        const start = rangeParts[0];
                        const end = rangeParts[1];
                        if (typeof start === 'number' && typeof end === 'number' &&
                            !isNaN(start) && !isNaN(end)) {
                            for (let i = start; i <= end; i++) {
                                weekNumbers.push(i);
                            }
                        }
                    }
                } else {
                    // Single week number
                    const weekNum = parseInt(trimmed, 10);
                    if (!isNaN(weekNum)) {
                        weekNumbers.push(weekNum);
                    }
                }
            }
        }

        return weekNumbers;
    }, []);

    // Parse initial week series to get selected weeks
    const initialSelectedWeeks = useMemo(() => {
        return parseWeekSeries(initialFormData.weekSeries || '');
    }, [initialFormData.weekSeries, parseWeekSeries]);

    // Initialize state with sensible defaults
    const [state, setState] = useState<AutoSchedulerState>({
        selectedWeeks: initialSelectedWeeks,
        selectedDays: activeDays, // Default to all active days
        minHour: institutionSettings.minHour,
        maxHour: institutionSettings.maxHour,
        selectedRooms: [], // Empty means all rooms
        weeksToSchedule: initialSelectedWeeks.length > 0 ? initialSelectedWeeks.length : 1,
        isGenerating: false,
        suggestions: [],
        error: null,
        sortBy: 'score',
        loadingProgress: 0,
        loadingMessage: '',
        generationStartTime: null,
        hasTimedOut: false,
        retryCount: 0,
        showFallbackOptions: false,
        isApplyingSuggestion: false,
        appliedSuggestion: null,
        showConfirmation: false
    });

    // Initialize AutoSchedulingService
    const autoSchedulingService = useMemo(() => new AutoSchedulingService(), []);

    // Get current subject for room filtering
    const currentSubject = useMemo(() => {
        return subjects.find(s => s.id === initialFormData.subject) || null;
    }, [subjects, initialFormData.subject]);

    // Note: Weeks do not need to be consecutive. UI will not enforce consecutiveness.

    // Convert selected weeks to a compact string (e.g., "1-3,5,7") for the week picker value
    const selectedWeeksString = useMemo(() => {
        const weeks = Array.from(new Set(state.selectedWeeks)).sort((a, b) => a - b);
        if (weeks.length === 0) return '';
        const ranges: string[] = [];
        let start = weeks[0]!;
        let end = start;
        for (let i = 1; i < weeks.length; i++) {
            const current = weeks[i]!;
            if (current === end + 1) {
                end = current;
            } else {
                ranges.push(start === end ? `${start}` : `${start}-${end}`);
                start = current;
                end = current;
            }
        }
        ranges.push(start === end ? `${start}` : `${start}-${end}`);
        return ranges.join(',');
    }, [state.selectedWeeks]);

    // Calculate duration from initial form data
    const duration = useMemo(() => {
        if (initialFormData.duration) {
            return initialFormData.duration;
        }

        // Calculate from timeStart and timeEnd if available
        if (initialFormData.timeStart && initialFormData.timeEnd) {
            const startTimeParts = initialFormData.timeStart.split(':').map(Number);
            const endTimeParts = initialFormData.timeEnd.split(':').map(Number);

            if (startTimeParts.length === 2 && endTimeParts.length === 2) {
                const startHour = startTimeParts[0];
                const startMin = startTimeParts[1];
                const endHour = endTimeParts[0];
                const endMin = endTimeParts[1];

                if (typeof startHour === 'number' && typeof startMin === 'number' &&
                    typeof endHour === 'number' && typeof endMin === 'number' &&
                    !isNaN(startHour) && !isNaN(startMin) && !isNaN(endHour) && !isNaN(endMin)) {

                    const startMinutes = startHour * 60 + startMin;
                    const endMinutes = endHour * 60 + endMin;
                    return endMinutes - startMinutes;
                }
            }
        }

        // Default to 90 minutes (typical class duration)
        return 90;
    }, [initialFormData]);

    // Compute if generation should be blocked and why (for disabling button + hint)
    const generationBlockReason = useMemo((): string | null => {
        // Not enough total eligible weeks selected
        if (state.weeksToSchedule > 0 && state.selectedWeeks.length < state.weeksToSchedule) {
            return `Not enough eligible weeks selected. Need ${state.weeksToSchedule}, selected ${state.selectedWeeks.length}.`;
        }

        // Time window too short to fit the duration
        const timeRangeMinutes = (state.maxHour - state.minHour) * 60;
        if (timeRangeMinutes < duration) {
            return `Time range is too short (${timeRangeMinutes} min) for the class duration (${duration} min).`;
        }

        return null;
    }, [state.weeksToSchedule, state.selectedWeeks.length, state.minHour, state.maxHour, duration]);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setState(prev => ({
                ...prev,
                selectedWeeks: initialSelectedWeeks,
                selectedDays: activeDays,
                minHour: institutionSettings.minHour,
                maxHour: institutionSettings.maxHour,
                selectedRooms: [],
                weeksToSchedule: initialSelectedWeeks.length > 0 ? initialSelectedWeeks.length : 1,
                isGenerating: false,
                suggestions: [],
                error: null,
                sortBy: 'score',
                loadingProgress: 0,
                loadingMessage: '',
                generationStartTime: null,
                hasTimedOut: false,
                retryCount: 0,
                showFallbackOptions: false,
                isApplyingSuggestion: false,
                appliedSuggestion: null,
                showConfirmation: false
            }));
        }
    }, [isOpen, activeDays, institutionSettings, initialSelectedWeeks]);

    // Enhanced validation functions with detailed error messages
    const validateConfiguration = useCallback((): string | null => {
        try {
            // Week validation
            if (state.selectedWeeks.length === 0) {
                return 'Please select at least one week using the week picker above. You can select individual weeks or ranges like "1-5".';
            }

            // Validate week numbers are reasonable
            const invalidWeeks = state.selectedWeeks.filter(week => week < 1 || week > 53);
            if (invalidWeeks.length > 0) {
                return `Invalid week numbers detected: ${invalidWeeks.join(', ')}. Week numbers must be between 1 and 53.`;
            }

            // Weeks-to-schedule validation
            if (typeof state.weeksToSchedule !== 'number' || state.weeksToSchedule < 1) {
                return 'Weeks to schedule must be at least 1.';
            }

            if (state.selectedWeeks.length < state.weeksToSchedule) {
                return `Not enough eligible weeks selected. You selected ${state.selectedWeeks.length} week(s) but requested ${state.weeksToSchedule} week(s).`;
            }

            // No requirement for consecutive weeks

            // Day validation
            if (state.selectedDays.length === 0) {
                return 'Please select at least one day by clicking the day buttons. All active days are selected by default.';
            }

            // Time range validation
            if (typeof state.minHour !== 'number' || typeof state.maxHour !== 'number') {
                return 'Invalid time range. Please select valid start and end hours.';
            }

            if (state.minHour >= state.maxHour) {
                return `Invalid time range: ${state.minHour}:00 to ${state.maxHour}:00. The start time must be earlier than the end time.`;
            }

            if (state.minHour < institutionSettings.minHour || state.maxHour > institutionSettings.maxHour) {
                return `Time range (${state.minHour}:00 - ${state.maxHour}:00) is outside institution hours (${institutionSettings.minHour}:00 - ${institutionSettings.maxHour}:00). Please adjust your time range.`;
            }

            // Check if time range is sufficient for the duration
            const timeRangeMinutes = (state.maxHour - state.minHour) * 60;
            if (timeRangeMinutes < duration) {
                return `Time range (${timeRangeMinutes} minutes) is too short for the class duration (${duration} minutes). Please expand your time range or check the class duration.`;
            }

            // Performance monitoring (informational only - no blocking)
            const estimatedCombinations = state.selectedWeeks.length *
                state.selectedDays.length *
                ((state.maxHour - state.minHour) * 2) *
                (state.selectedRooms.length || availableRooms.length);

            if (estimatedCombinations > 10000) {
                console.log(`[AutoScheduler] Large parameter set: ${estimatedCombinations.toLocaleString()} combinations - this may take longer to process`);
            }

            // Room validation
            if (state.selectedRooms.length > 0) {
                const availableRoomNames = availableRooms.map(r => r.name).filter(name => name);
                const invalidRooms = state.selectedRooms.filter(room => !availableRoomNames.includes(room));
                if (invalidRooms.length > 0) {
                    return `Selected rooms are not available: ${invalidRooms.join(', ')}. Please refresh the room list or select different rooms.`;
                }
            }

            // Data validation with helpful error messages
            if (!Array.isArray(teachers) || teachers.length === 0) {
                return 'Teacher data is not available. Please refresh the page and try again.';
            }

            const teacherExists = teachers.some(t => t.id?.toString() === initialFormData.teacher);
            if (!teacherExists) {
                return `Selected teacher (${initialFormData.teacher}) is not available. Please select a different teacher or refresh the data.`;
            }

            if (!Array.isArray(subjects) || subjects.length === 0) {
                return 'Subject data is not available. Please refresh the page and try again.';
            }

            const subjectExists = subjects.some(s => s.id === initialFormData.subject || s.name === initialFormData.subject);
            if (!subjectExists) {
                return `Selected subject (${initialFormData.subject}) is not available. Please select a different subject or refresh the data.`;
            }

            if (!Array.isArray(groups) || groups.length === 0) {
                return 'Group data is not available. Please refresh the page and try again.';
            }

            const availableGroupIds = groups.map(g => g.id?.toString()).filter(id => id);
            const invalidGroups = initialFormData.groups.filter(groupId =>
                !availableGroupIds.includes(groupId.toString())
            );
            if (invalidGroups.length > 0) {
                return `Selected groups are not available: ${invalidGroups.join(', ')}. Please select different groups or refresh the data.`;
            }

            // Validate available rooms
            if (!Array.isArray(availableRooms) || availableRooms.length === 0) {
                return 'No rooms are available for scheduling. Please ensure room data is loaded or contact your administrator.';
            }

            return null;

        } catch (error) {
            console.error('[AutoScheduler] Error in validateConfiguration:', error);
            return 'An error occurred while validating your configuration. Please refresh the page and try again.';
        }
    }, [state, institutionSettings, duration, availableRooms, teachers, subjects, groups, initialFormData]);

    // Handle week selection
    const handleWeekChange = useCallback((weekSeries: string) => {
        const weekNumbers = parseWeekSeries(weekSeries);

        setState(prev => ({
            ...prev,
            selectedWeeks: weekNumbers,
            // Update weeksToSchedule to match the number of selected weeks
            weeksToSchedule: weekNumbers.length > 0 ? weekNumbers.length : 1
        }));
    }, [parseWeekSeries]);



    // Handle weeks-to-schedule change
    const handleWeeksToScheduleChange = useCallback((value: number) => {
        const sanitized = Math.max(1, Math.min(52, Math.floor(value)));
        setState(prev => ({ ...prev, weeksToSchedule: sanitized }));
    }, []);

    // Handle day selection
    const handleDayChange = useCallback((selectedDays: string[]) => {
        setState(prev => ({ ...prev, selectedDays }));
    }, []);

    // Handle time range change
    const handleTimeRangeChange = useCallback((minHour: number, maxHour: number, isValid: boolean) => {
        setState(prev => ({
            ...prev,
            minHour,
            maxHour,
            error: isValid ? null : 'Invalid time range'
        }));
    }, []);

    // Handle room selection
    const handleRoomChange = useCallback((selectedRooms: string[]) => {
        setState(prev => ({ ...prev, selectedRooms }));
    }, []);

    // Handle sort change
    const handleSortChange = useCallback((sortBy: 'score' | 'time' | 'room') => {
        setState(prev => ({ ...prev, sortBy }));
    }, []);

    // Handle retry with optimized parameters
    const handleRetryOptimized = useCallback(() => {
        // Reduce parameter scope for better performance while still generating all combinations
        setState(prev => ({
            ...prev,
            selectedWeeks: prev.selectedWeeks.slice(0, 3), // Limit to 3 weeks
            selectedDays: prev.selectedDays.slice(0, 4), // Limit to 4 days
            maxHour: Math.min(prev.maxHour, prev.minHour + 10), // Limit to 10-hour range
            selectedRooms: prev.selectedRooms.slice(0, 8), // Limit to 8 rooms
            retryCount: 0,
            hasTimedOut: false,
            showFallbackOptions: false,
            error: null
        }));

        // Trigger generation after state update
        setShouldRetryGeneration(true);
    }, []);

    // Handle manual scheduling fallback
    const handleManualScheduling = useCallback(() => {
        console.log('[AutoScheduler] User chose manual scheduling fallback');
        onClose();
    }, [onClose]);

    // Handle simplified search
    const handleSimplifiedSearch = useCallback(() => {
        // Reset to most basic parameters while still generating all combinations
        setState(prev => ({
            ...prev,
            selectedWeeks: prev.selectedWeeks.slice(0, 2), // Two weeks for variety
            selectedDays: prev.selectedDays.slice(0, 3), // Three days for variety
            minHour: Math.max(prev.minHour, 9), // Start at 9 AM
            maxHour: Math.min(prev.maxHour, 17), // End at 5 PM
            selectedRooms: [], // All rooms
            error: null,
            showFallbackOptions: false,
            retryCount: 0,
            hasTimedOut: false
        }));
    }, []);

    // Trigger generation after retry optimization
    const [shouldRetryGeneration, setShouldRetryGeneration] = useState(false);

    // Validate suggestion data before application
    const validateSuggestion = useCallback((suggestion: ScheduleSuggestion): string | null => {
        // Check required fields
        if (!suggestion) {
            return 'Suggestion is null or undefined';
        }

        if (!suggestion.id || typeof suggestion.id !== 'string') {
            return 'Suggestion ID is missing or invalid';
        }

        if (!suggestion.day || typeof suggestion.day !== 'string') {
            return 'Day is missing or invalid';
        }

        if (!suggestion.timeStart || typeof suggestion.timeStart !== 'string') {
            return 'Start time is missing or invalid';
        }

        if (!suggestion.timeEnd || typeof suggestion.timeEnd !== 'string') {
            return 'End time is missing or invalid';
        }

        if (!suggestion.room || typeof suggestion.room !== 'string') {
            return 'Room is missing or invalid';
        }

        if (typeof suggestion.week !== 'number' || suggestion.week < 1) {
            return 'Week number is missing or invalid';
        }

        if (typeof suggestion.year !== 'number' || suggestion.year < 2000) {
            return 'Year is missing or invalid';
        }

        if (typeof suggestion.conflictScore !== 'number' || suggestion.conflictScore < 0) {
            return 'Conflict score is missing or invalid';
        }

        // Validate day format
        const validDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        if (!validDays.includes(suggestion.day.toLowerCase())) {
            return `Invalid day: ${suggestion.day}. Must be one of: ${validDays.join(', ')}`;
        }

        // Validate time format (HH:MM)
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(suggestion.timeStart)) {
            return `Invalid start time format: ${suggestion.timeStart}. Expected HH:MM format`;
        }

        if (!timeRegex.test(suggestion.timeEnd)) {
            return `Invalid end time format: ${suggestion.timeEnd}. Expected HH:MM format`;
        }

        // Validate time logic (start < end)
        const startParts = suggestion.timeStart.split(':');
        const endParts = suggestion.timeEnd.split(':');

        // Ensure we have exactly 2 parts for each time
        if (startParts.length !== 2 || endParts.length !== 2) {
            return `Invalid time format: unable to parse ${suggestion.timeStart} or ${suggestion.timeEnd}`;
        }

        // Parse and validate the numeric values
        const startHour = parseInt(startParts[0] || '', 10);
        const startMin = parseInt(startParts[1] || '', 10);
        const endHour = parseInt(endParts[0] || '', 10);
        const endMin = parseInt(endParts[1] || '', 10);

        if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) {
            return `Invalid time format: unable to parse ${suggestion.timeStart} or ${suggestion.timeEnd}`;
        }

        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        if (startMinutes >= endMinutes) {
            return `Invalid time range: ${suggestion.timeStart} - ${suggestion.timeEnd}. Start time must be before end time`;
        }

        // Validate duration matches expected duration
        const suggestionDuration = endMinutes - startMinutes;
        if (Math.abs(suggestionDuration - duration) > 5) { // Allow 5-minute tolerance
            return `Duration mismatch: suggestion is ${suggestionDuration} minutes, expected ${duration} minutes`;
        }

        // Validate time is within institution bounds
        if (startHour < institutionSettings.minHour || endHour > institutionSettings.maxHour) {
            return `Time ${suggestion.timeStart}-${suggestion.timeEnd} is outside institution hours (${institutionSettings.minHour}:00-${institutionSettings.maxHour}:00)`;
        }

        // Validate room exists in available rooms
        const roomExists = availableRooms.some(room => room.name === suggestion.room);
        if (!roomExists) {
            return `Room ${suggestion.room} is not available`;
        }

        // Validate week is in selected weeks (if any were selected)
        if (state.selectedWeeks.length > 0 && !state.selectedWeeks.includes(suggestion.week)) {
            return `Week ${suggestion.week} was not in the selected weeks: ${state.selectedWeeks.join(', ')}`;
        }

        // Validate day is in selected days
        if (!state.selectedDays.map(d => d.toLowerCase()).includes(suggestion.day.toLowerCase())) {
            return `Day ${suggestion.day} was not in the selected days: ${state.selectedDays.join(', ')}`;
        }

        // Validate conflicts array
        if (!Array.isArray(suggestion.conflicts)) {
            return 'Conflicts must be an array';
        }

        // Validate each conflict detail
        for (let i = 0; i < suggestion.conflicts.length; i++) {
            const conflict = suggestion.conflicts[i];
            if (!conflict || !conflict.type || !conflict.severity || !conflict.description || typeof conflict.points !== 'number') {
                return `Invalid conflict at index ${i}: missing required fields`;
            }

            const validTypes = ['teacher', 'group', 'room', 'holiday', 'subject-room'];
            if (!validTypes.includes(conflict.type)) {
                return `Invalid conflict type at index ${i}: ${conflict.type}`;
            }

            const validSeverities = ['orange', 'red'];
            if (!validSeverities.includes(conflict.severity)) {
                return `Invalid conflict severity at index ${i}: ${conflict.severity}`;
            }

            if (conflict.points < 0) {
                return `Invalid conflict points at index ${i}: ${conflict.points}`;
            }
        }

        // Validate isOptimal flag matches conflict score
        const expectedOptimal = suggestion.conflictScore === 0;
        if (suggestion.isOptimal !== expectedOptimal) {
            return `Inconsistent optimal flag: isOptimal=${suggestion.isOptimal} but conflictScore=${suggestion.conflictScore}`;
        }

        // Validate year matches current year
        if (suggestion.year !== initialFormData.year) {
            return `Year mismatch: suggestion year ${suggestion.year} does not match form year ${initialFormData.year}`;
        }

        return null; // All validations passed
    }, [duration, institutionSettings, availableRooms, state.selectedWeeks, state.selectedDays, initialFormData.year]);



    // Generate suggestions with comprehensive error handling
    const handleGenerateSuggestions = useCallback(async () => {
        const validationError = validateConfiguration();
        if (validationError) {
            setState(prev => ({ ...prev, error: validationError, showFallbackOptions: false }));
            return;
        }

        // Validate required data before proceeding
        const dataValidationError = validateRequiredData({
            teachers,
            groups,
            subjects,
            rooms: availableRooms,
            scheduleItems: baseScheduleItems
        });

        if (dataValidationError) {
            setState(prev => ({
                ...prev,
                error: dataValidationError,
                showFallbackOptions: true
            }));
            return;
        }

        const startTime = Date.now();
        const TIMEOUT_MS = 60000; // 1 minute timeout for generating all combinations
        const MAX_RETRIES = 2;

        setState(prev => ({
            ...prev,
            isGenerating: true,
            error: null,
            suggestions: [],
            loadingProgress: 0,
            loadingMessage: 'Initializing...',
            generationStartTime: startTime,
            hasTimedOut: false,
            showFallbackOptions: false
        }));

        // Create timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => {
                reject(new Error('Suggestion generation timed out. Generating all possible combinations may take longer than expected.'));
            }, TIMEOUT_MS);
        });

        try {
            // Prepare suggestion parameters
            const parameters: SuggestionParameters = {
                weeks: state.selectedWeeks,
                days: state.selectedDays,
                minHour: state.minHour,
                maxHour: state.maxHour,
                rooms: state.selectedRooms.length > 0 ? state.selectedRooms : availableRooms.map(r => r.name),
                duration,
                teacher: initialFormData.teacher,
                subject: initialFormData.subject,
                groups: initialFormData.groups,
                year: initialFormData.year,
                weeksToSchedule: state.weeksToSchedule,
                excludeItemId: excludeItemId // Pass the new prop
            };

            console.log('[AutoScheduler] Generating suggestions with parameters:', parameters);

            // Performance monitoring
            const estimatedCombinations = parameters.weeks.length *
                parameters.days.length *
                ((parameters.maxHour - parameters.minHour) * 2) *
                parameters.rooms.length;

            console.log(`[AutoScheduler] Estimated combinations: ${estimatedCombinations}`);

            // Show performance warning for large parameter sets
            if (estimatedCombinations > 5000) {
                setState(prev => ({
                    ...prev,
                    loadingMessage: `Analyzing ALL ${estimatedCombinations.toLocaleString()} combinations. This may take a while...`
                }));
            }

            // Generate suggestions with progress callback and timeout
            const suggestionPromise = autoSchedulingService.generateSuggestions(
                parameters,
                baseScheduleItems,
                teachers,
                groups,
                subjects,
                (progress) => {
                    const elapsed = Date.now() - startTime;
                    const estimatedTotal = progress.total > 0 ? (elapsed / progress.current) * progress.total : elapsed;
                    const remainingTime = Math.max(0, estimatedTotal - elapsed);

                    setState(prev => ({
                        ...prev,
                        loadingProgress: (progress.current / progress.total) * 100,
                        loadingMessage: `${progress.message} (${Math.round(remainingTime / 1000)}s remaining)`
                    }));
                }
            );

            // Race between suggestion generation and timeout
            const suggestions = await Promise.race([suggestionPromise, timeoutPromise]);

            const elapsed = Date.now() - startTime;
            console.log(`[AutoScheduler] Generated ${suggestions.length} suggestions in ${elapsed}ms`);

            setState(prev => ({
                ...prev,
                suggestions,
                isGenerating: false,
                loadingProgress: 100,
                loadingMessage: `Complete! Found ${suggestions.length} suggestions in ${(elapsed / 1000).toFixed(1)}s`,
                retryCount: 0
            }));

        } catch (error) {
            const elapsed = Date.now() - startTime;

            // Create error context for logging
            const errorContext: ErrorContext = {
                operation: 'generateSuggestions',
                parameters: {
                    weeks: state.selectedWeeks.length,
                    days: state.selectedDays.length,
                    timeRange: `${state.minHour}-${state.maxHour}`,
                    rooms: state.selectedRooms.length || availableRooms.length,
                    teacher: initialFormData.teacher,
                    subject: initialFormData.subject,
                    groups: initialFormData.groups.length
                },
                timestamp: startTime,
                userAgent: navigator.userAgent
            };

            // Use enhanced error analysis
            const analysis = analyzeError(error instanceof Error ? error : String(error), errorContext);

            // Log error with context
            logError(error instanceof Error ? error : new Error(String(error)), errorContext, analysis);

            // Check if we have partial results to preserve
            const hasPartialResults = state.suggestions.length > 0;

            if (hasPartialResults) {
                console.log(`[AutoScheduler] Preserving ${state.suggestions.length} partial results despite error`);

                // Sort the partial results by conflict score
                const sortedPartialResults = [...state.suggestions].sort((a, b) => a.conflictScore - b.conflictScore);

                // Update state to show partial results with informative message
                setState(prev => ({
                    ...prev,
                    suggestions: sortedPartialResults,
                    isGenerating: false,
                    loadingProgress: 100,
                    loadingMessage: `Partial results: Found ${sortedPartialResults.length} suggestions before ${analysis.category === 'timeout' ? 'timeout' : 'error'}`,
                    error: `Generation ${analysis.category === 'timeout' ? 'timed out' : 'encountered an error'} after finding ${sortedPartialResults.length} suggestions. Here are the best ones found so far.`,
                    showFallbackOptions: true,
                    hasTimedOut: analysis.category === 'timeout'
                }));

                return; // Don't show fallback options since we have results
            }

            // Handle retries for appropriate error types (only if no partial results)
            if (analysis.shouldRetry && state.retryCount < MAX_RETRIES) {
                const retryDelay = analysis.retryDelay || 2000;
                console.log(`[AutoScheduler] Retrying due to ${analysis.category} error (attempt ${state.retryCount + 1}/${MAX_RETRIES}) in ${retryDelay}ms`);

                setState(prev => ({
                    ...prev,
                    retryCount: prev.retryCount + 1,
                    loadingMessage: `${analysis.category} error. Retrying in ${Math.ceil(retryDelay / 1000)} seconds... (${prev.retryCount + 1}/${MAX_RETRIES})`
                }));

                setTimeout(() => {
                    handleGenerateSuggestions();
                }, retryDelay);
                return;
            }

            // Format error message for user display
            let finalErrorMessage = formatErrorMessage(analysis, true);

            // Add performance context if generation took a long time
            if (elapsed > 10000) {
                finalErrorMessage += `\n\nNote: Generation took ${Math.round(elapsed / 1000)} seconds, which may indicate performance issues.`;
            }

            // Add retry information if max retries reached
            if (state.retryCount >= MAX_RETRIES && analysis.shouldRetry) {
                finalErrorMessage += `\n\nMaximum retry attempts (${MAX_RETRIES}) reached.`;
            }

            setState(prev => ({
                ...prev,
                error: finalErrorMessage,
                isGenerating: false,
                loadingProgress: 0,
                loadingMessage: '',
                showFallbackOptions: analysis.shouldShowFallback,
                hasTimedOut: analysis.category === 'timeout'
            }));
        }
    }, [
        validateConfiguration,
        state.selectedWeeks,
        state.selectedDays,
        state.minHour,
        state.maxHour,
        state.selectedRooms,
        state.retryCount,
        availableRooms,
        duration,
        initialFormData,
        baseScheduleItems,
        teachers,
        groups,
        subjects,
        autoSchedulingService,
        excludeItemId
    ]);

    // Trigger generation after retry optimization
    useEffect(() => {
        if (shouldRetryGeneration) {
            setShouldRetryGeneration(false);
            handleGenerateSuggestions();
        }
    }, [shouldRetryGeneration, handleGenerateSuggestions]);

    // Ref and auto-scroll to suggestions when available
    const suggestionsRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!state.isGenerating && state.suggestions.length > 0 && suggestionsRef.current) {
            // Scroll smoothly to the suggestions list
            suggestionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [state.isGenerating, state.suggestions.length]);

    // Handle suggestion selection with validation and feedback
    const handleSuggestionSelect = useCallback(async (suggestion: ScheduleSuggestion) => {
        console.log('[AutoScheduler] Suggestion selected:', suggestion);

        // Validate suggestion data
        const validationError = validateSuggestion(suggestion);
        if (validationError) {
            console.error('[AutoScheduler] Invalid suggestion:', validationError);
            setState(prev => ({
                ...prev,
                error: `Invalid suggestion: ${validationError}`,
                showFallbackOptions: false,
                isApplyingSuggestion: false
            }));
            return;
        }

        try {
            // Set applying state
            setState(prev => ({
                ...prev,
                isApplyingSuggestion: true,
                error: null,
                appliedSuggestion: suggestion,
                showConfirmation: false
            }));

            // Log successful selection for debugging
            console.log('[AutoScheduler] Applying suggestion:', {
                day: suggestion.day,
                timeStart: suggestion.timeStart,
                timeEnd: suggestion.timeEnd,
                room: suggestion.room,
                week: suggestion.week,
                conflictScore: suggestion.conflictScore
            });

            // Show brief confirmation feedback
            setState(prev => ({
                ...prev,
                showConfirmation: true
            }));

            // Brief delay to show confirmation
            await new Promise(resolve => setTimeout(resolve, 800));

            // Call parent handler with validated suggestion
            onSuggestionSelect(suggestion);

            // Close modal after successful selection
            onClose();

        } catch (error) {
            console.error('[AutoScheduler] Error applying suggestion:', error);
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : 'Failed to apply suggestion',
                showFallbackOptions: true,
                isApplyingSuggestion: false,
                appliedSuggestion: null,
                showConfirmation: false
            }));
        }
    }, [validateSuggestion, onSuggestionSelect, onClose]);

    // Handle modal close
    const handleClose = useCallback(() => {
        if (!state.isGenerating && !state.isApplyingSuggestion) {
            onClose();
        }
    }, [state.isGenerating, state.isApplyingSuggestion, onClose]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen && !state.isGenerating && !state.isApplyingSuggestion) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }

        // Return empty cleanup function when not open
        return () => { };
    }, [isOpen, state.isGenerating, state.isApplyingSuggestion, handleClose]);

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Generate All Schedule Possibilities"
        >
            <div className="space-y-6">
                {/* Fixed Parameters Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Fixed Parameters</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="font-medium text-gray-600">Teacher:</span>
                            <span className="ml-2 text-gray-900">
                                {teachers.find(t => t.id.toString() === initialFormData.teacher)?.name || initialFormData.teacher}
                            </span>
                        </div>
                        <div>
                            <span className="font-medium text-gray-600">Duration:</span>
                            <span className="ml-2 text-gray-900">{duration} minutes</span>
                        </div>
                        <div>
                            <span className="font-medium text-gray-600">Subject:</span>
                            <span className="ml-2 text-gray-900">
                                {subjects.find(s => s.id === initialFormData.subject)?.name || initialFormData.subject}
                            </span>
                        </div>

                        <div>
                            <span className="font-medium text-gray-600">Groups:</span>
                            <span className="ml-2 text-gray-900">
                                {initialFormData.groups.map(groupId => {
                                    const group = groups.find(g => g.id?.toString() === groupId.toString());
                                    return group?.name || groupId;
                                }).join(', ')}
                            </span>
                        </div>

                    </div>
                </div>

                {/* Configuration Section */}
                <div className="space-y-4">
                    {/* Week Selection */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                            Eligible Weeks
                        </label>
                        <ScheduleEntryWeekPicker
                            value={selectedWeeksString}
                            onChange={handleWeekChange}
                            currentYear={currentYear}
                            className="w-full"
                        />
                        <div className="mt-3 flex items-center gap-3">
                            <label className="text-sm font-medium text-gray-700" htmlFor="weeks-to-schedule-input">
                                How Many Weeks to schedule
                            </label>
                            <input
                                id="weeks-to-schedule-input"
                                type="number"
                                min={1}
                                max={52}
                                value={state.weeksToSchedule}
                                onChange={(e) => handleWeeksToScheduleChange(Number(e.target.value) || 1)}
                                className="w-24 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                aria-label="Number of weeks to schedule"
                                title="Number of weeks to schedule"
                            />
                        </div>
                        {/* No consecutive-week requirement */}
                    </div>

                    {/* Day Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Days
                        </label>
                        <DaySelector
                            activeDays={activeDays}
                            selectedDays={state.selectedDays}
                            onChange={handleDayChange}
                        />
                    </div>

                    {/* Time Range */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Time Range
                        </label>
                        <TimeRangeSelector
                            minHour={state.minHour}
                            maxHour={state.maxHour}
                            institutionMinHour={institutionSettings.minHour}
                            institutionMaxHour={institutionSettings.maxHour}
                            onChange={handleTimeRangeChange}
                        />
                    </div>

                    {/* Room Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rooms
                        </label>
                        <RoomSelector
                            availableRooms={availableRooms}
                            selectedRooms={state.selectedRooms}
                            onChange={handleRoomChange}
                            subject={currentSubject}
                        />
                    </div>
                </div>

                {/* Error Display */}
                {state.error && (
                    <div className={`border rounded-md p-4 ${state.suggestions.length > 0
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-red-50 border-red-200'
                        }`}>
                        <div className="flex">
                            <div className="flex-shrink-0">
                                {state.suggestions.length > 0 ? (
                                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </div>
                            <div className="ml-3">
                                <h3 className={`text-sm font-medium ${state.suggestions.length > 0 ? 'text-blue-800' : 'text-red-800'
                                    }`}>
                                    {state.suggestions.length > 0
                                        ? 'Partial Results Available'
                                        : (state.hasTimedOut ? 'Generation Timed Out' : 'Generation Error')
                                    }
                                </h3>
                                <div className={`mt-2 text-sm ${state.suggestions.length > 0 ? 'text-blue-700' : 'text-red-700'
                                    }`}>
                                    {state.error}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Fallback Options - Only show when no partial results */}
                {state.showFallbackOptions && state.suggestions.length === 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-yellow-800">
                                    Try Alternative Options
                                </h3>
                                <div className="mt-2 text-sm text-yellow-700">
                                    The automatic suggestion generation encountered issues. You can try these alternatives:
                                </div>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <Button
                                        onClick={handleRetryOptimized}
                                        className="text-xs px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                                        disabled={state.isGenerating}
                                        title="Try again with reduced parameters for better performance"
                                        aria-label="Try again with optimized parameters"
                                    >
                                        Try Again with Fewer Parameters
                                    </Button>
                                    <Button
                                        onClick={handleSimplifiedSearch}
                                        className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        disabled={state.isGenerating}
                                        title="Reset to basic search parameters for faster results"
                                        aria-label="Simplify search parameters"
                                    >
                                        Simplify Search
                                    </Button>
                                    <Button
                                        onClick={handleManualScheduling}
                                        className="text-xs px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                                        disabled={state.isGenerating}
                                        title="Close and schedule manually"
                                        aria-label="Switch to manual scheduling"
                                    >
                                        Manual Scheduling
                                    </Button>
                                </div>
                                <div className="mt-3 text-xs text-yellow-600">
                                    <strong>Tips:</strong> The system analyzes all possible combinations and preserves partial results. Large parameter sets may take longer to process.
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Generate Button */}
                <div className="flex justify-center gap-3">
                    <Button
                        onClick={handleGenerateSuggestions}
                        disabled={state.isGenerating || (!!state.error && !state.showFallbackOptions) || !!generationBlockReason}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        title={
                            state.isGenerating
                                ? 'Generating all possible schedule suggestions...'
                                : generationBlockReason || 'Generate all possible schedule suggestions'
                        }
                        aria-label={state.isGenerating ? 'Generating all possible schedule suggestions' : (generationBlockReason ? `Disabled: ${generationBlockReason}` : 'Generate all possible schedule suggestions')}
                    >
                        {state.isGenerating && (
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        )}
                        {state.isGenerating ? 'Generating...' : 'Generate All Suggestions'}
                    </Button>

                    {state.isGenerating && (
                        <Button
                            onClick={() => {
                                setState(prev => ({
                                    ...prev,
                                    isGenerating: false,
                                    loadingProgress: 0,
                                    loadingMessage: '',
                                    error: 'Generation cancelled by user',
                                    showFallbackOptions: true
                                }));
                            }}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                            title="Cancel suggestion generation"
                            aria-label="Cancel suggestion generation"
                        >
                            Cancel
                        </Button>
                    )}
                </div>

                {/* Disabled reason hint */}
                {!!generationBlockReason && !state.isGenerating && (
                    <div className="mt-2 text-center text-xs text-red-600">
                        {generationBlockReason}
                    </div>
                )}

                {/* Confirmation Feedback */}
                {state.showConfirmation && state.appliedSuggestion && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-green-800">
                                    Suggestion Applied Successfully!
                                </h3>
                                <div className="mt-2 text-sm text-green-700">
                                    <div className="font-medium">
                                        {(() => {
                                            const formatTime = (time: string) => {
                                                const parts = time.split(':');
                                                if (parts.length !== 2) return time; // Return original if parsing fails
                                                const hours = parts[0];
                                                const minutes = parts[1];
                                                if (!hours || !minutes) return time; // Return original if parsing fails
                                                const hour = parseInt(hours, 10);
                                                if (isNaN(hour)) return time;
                                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                                const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                                return `${displayHour}:${minutes} ${ampm}`;
                                            };

                                            const formatDay = (day: string) => {
                                                const dayMap: { [key: string]: string } = {
                                                    'monday': 'Monday',
                                                    'tuesday': 'Tuesday',
                                                    'wednesday': 'Wednesday',
                                                    'thursday': 'Thursday',
                                                    'friday': 'Friday',
                                                    'saturday': 'Saturday',
                                                    'sunday': 'Sunday'
                                                };
                                                return dayMap[day.toLowerCase()] || day;
                                            };

                                            return `${formatDay(state.appliedSuggestion.day)}, ${formatTime(state.appliedSuggestion.timeStart)} - ${formatTime(state.appliedSuggestion.timeEnd)} in Room ${state.appliedSuggestion.room}`;
                                        })()}
                                    </div>
                                    <div className="mt-1">
                                        {state.appliedSuggestion.conflictScore === 0
                                            ? 'Perfect match with no conflicts!'
                                            : `Applied with ${state.appliedSuggestion.conflictScore} conflict point${state.appliedSuggestion.conflictScore === 1 ? '' : 's'}.`
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Suggestions Section */}
                {(state.isGenerating || state.suggestions.length > 0) && (
                    <div ref={suggestionsRef} className="border-t pt-6">
                        {state.suggestions.length > 0 && !state.isGenerating && state.error && (
                            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3">
                                <div className="flex items-center text-sm text-blue-700">
                                    <svg className="h-4 w-4 text-blue-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                    </svg>
                                    <span>
                                        <strong>Partial Results:</strong> These are the best suggestions found before the process {state.hasTimedOut ? 'timed out' : 'encountered an error'}.
                                        You can use these results or try again with different parameters.
                                    </span>
                                </div>
                            </div>
                        )}
                        <SuggestionList
                            suggestions={state.suggestions}
                            onSelect={handleSuggestionSelect}
                            sortBy={state.sortBy}
                            onSortChange={handleSortChange}
                            isLoading={state.isGenerating}
                            loadingProgress={state.loadingProgress}
                            loadingMessage={state.loadingMessage}
                            disabled={state.isApplyingSuggestion}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default AutoScheduler;